import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { Logger } from '@/shared/logger/logger';

import { IpLocationData } from '../ip_data/types';
import { StorageHandler } from '../storage_handler';
import {
  CustomerDataFields,
  ICustomerDataFields,
  TCustomerDataArrays,
} from './types';

/**
 * Result of updating customer data with IP location
 * @property {ICustomerDataFields[]} currentCustomerSessionData - Current session data after update
 * @property {boolean} updated - Indicates if the data was updated
 */
export type TMaybeUpdateWithIpLocation = {
  currentCustomerSessionData: ICustomerDataFields[];
  updated: boolean;
};

/**
 * Set of unique customer data fields
 * @property {Set<string>} [as] - Data sources
 * @property {Set<string>} [cc] - Country codes
 * @property {Set<string>} [ct] - Cities
 * @property {Set<string>} [dob] - Dates of birth
 * @property {Set<string>} [em] - Email addresses
 * @property {Set<string>} [fn] - First names
 * @property {Set<string>} [ge] - Genders
 * @property {Set<string>} [id] - Identifiers
 * @property {Set<string>} [ln] - Last names
 * @property {Set<string>} [ph] - Phone numbers
 * @property {Set<string>} [rc] - Region codes
 * @property {Set<string>} [rg] - Regions
 * @property {Set<string>} [street] - Street addresses
 * @property {Set<number | string>} [zip] - Zip codes
 */
type CustomerDataSet = {
  as?: Set<string>;
  cc?: Set<string>;
  ct?: Set<string>;
  dob?: Set<string>;
  em?: Set<string>;
  fn?: Set<string>;
  ge?: Set<string>;
  id?: Set<string>;
  ln?: Set<string>;
  ph?: Set<string>;
  rc?: Set<string>;
  rg?: Set<string>;
  street?: Set<string>;
  zip?: Set<number | string>;
};

/**
 * Handles unified customer data across sessions and identity
 */
export class UnifiedCustomerDataHandler {
  private static readonly identityStorageHandler = new StorageHandler<
    ICustomerDataFields[]
  >('identityCustomerData', [], EBrowserStorageType.LOCALSTORAGE);
  private static readonly sessionStorageHandler = new StorageHandler<
    ICustomerDataFields[]
  >('sessionCustomerData', [], EBrowserStorageType.LOCALSTORAGE);

  /**
   * Starts a new session by clearing session storage
   */
  static startNewSession(): void {
    this.sessionStorageHandler.set([]);
  }

  /**
   * Updates identity data with new customer data
   * @param {ICustomerDataFields[]} customerData - New customer data to be added
   * @returns {Object} Object containing merged customer data, whether new data was added, and current session data
   * @property {TCustomerDataArrays} mergedCustomerData - Merged customer data
   * @property {boolean} newDataAdded - Indicates if new data was added
   * @property {ICustomerDataFields[]} newSessionData - Current session data
   */
  static updateIdentity(customerData: ICustomerDataFields[]): {
    mergedCustomerData: TCustomerDataArrays;
    newDataAdded: boolean;
    newSessionData: ICustomerDataFields[];
  } {
    const validCustomerData = this.filterValidData(customerData);
    const currentIdentityData = this.identityStorageHandler.get() || [];
    let newDataAdded = false;

    const newUniqueCustomerData = validCustomerData.filter(
      data =>
        !this.isDuplicate(
          data as CustomerDataFields,
          currentIdentityData as CustomerDataFields[],
        ),
    );
    if (newUniqueCustomerData.length > 0) {
      currentIdentityData.push(...newUniqueCustomerData);
      this.identityStorageHandler.set(currentIdentityData);
      newDataAdded = true;
    }

    return {
      mergedCustomerData: this.mergeCustomerData(currentIdentityData),
      newDataAdded,
      newSessionData: this.sessionStorageHandler.get(),
    };
  }
  /**
   * Initializes or updates customer data
   * @param {ICustomerDataFields[]} customerData - Customer data to initialize or update
   * @returns {Object} Merged data, update status, and current session data
   */
  static initOrMaybeUpdate(customerData: ICustomerDataFields[]): {
    mergedCustomerData: TCustomerDataArrays;
    newDataAdded: boolean;
    newSessionData: ICustomerDataFields[];
  } {
    const validData = this.filterValidData(customerData);
    let sessionData = this.sessionStorageHandler.get() || [];
    let identityData = this.identityStorageHandler.get() || [];
    let newDataAdded = false;
    const ipLocationData = Array.isArray(validData)
      ? validData.find(data => data?.ds === 'ip')
      : undefined;

    if (Array.isArray(validData)) {
      // Update session and identity data
      validData.forEach(data => {
        if (
          !this.isDuplicate(
            data as CustomerDataFields,
            sessionData as CustomerDataFields[],
          )
        ) {
          sessionData.push(data);
          newDataAdded = true;
        }
        if (
          data.ds !== 'ip' &&
          !this.isDuplicate(
            data as CustomerDataFields,
            identityData as CustomerDataFields[],
          )
        ) {
          identityData.push(data);
        }
      });
    } else {
      Logger.error('Invalid customer data', {
        validData,
      });
    }
    if (!Array.isArray(sessionData)) {
      // sessionData = [];
      Logger.error('Invalid session data', {
        sessionData,
      });
    }

    // Replace existing IP data in session
    sessionData = ipLocationData
      ? sessionData?.filter(d => d.ds !== 'ip').concat(ipLocationData)
      : sessionData;

    this.sessionStorageHandler.set(sessionData);
    this.identityStorageHandler.set(identityData);

    const mergedData = this.mergeCustomerData(identityData);

    // Use IP location data if identity location is empty
    const locationFields = ['ct', 'zip', 'rg'] as const;
    const isIdentityLocationEmpty = locationFields.every(
      field => !mergedData[field]?.length,
    );
    const ipSessionData = Array.isArray(sessionData)
      ? sessionData.find(data => data.ds === 'ip')
      : undefined;
    if (isIdentityLocationEmpty && ipSessionData) {
      let locationFieldsPlusCC = [...locationFields, 'cc'] as const;
      locationFieldsPlusCC.forEach(field => {
        const value = ipSessionData[field];
        if (value !== undefined) {
          mergedData[field] = [field === 'zip' ? value.toString() : value];
        }
      });
      mergedData.ds?.push('ip');
    }

    return {
      mergedCustomerData: mergedData,
      newDataAdded,
      newSessionData: sessionData,
    };
  }

  /**
   * Updates session data with IP location if necessary
   * @param {Partial<ICustomerDataFields>} ipLocationData - IP location data to potentially update with
   * @returns {TMaybeUpdateWithIpLocation} Object containing current session data and whether it was updated
   */
  static maybeUpdateWithIpLocation(
    ipLocationData: Partial<IpLocationData>,
  ): TMaybeUpdateWithIpLocation {
    let sessionCustomerData = this.sessionStorageHandler.get() || [];
    const isSessionEmptyOrIpOnly =
      sessionCustomerData.length === 0 ||
      sessionCustomerData[0]?.ds === 'ip' ||
      sessionCustomerData[0]?.ds === 'empty';

    if (isSessionEmptyOrIpOnly) {
      const ipLocationCustData: Partial<ICustomerDataFields> = {
        ...ipLocationData,
        ds: 'ip',
        zip: ipLocationData?.zip ? String(ipLocationData?.zip) : undefined,
      };
      const validData = this.filterValidData([
        ipLocationCustData as ICustomerDataFields,
      ]);
      this.sessionStorageHandler.set(validData);
      return {
        currentCustomerSessionData: validData,
        updated: true,
      };
    }

    return {
      currentCustomerSessionData: sessionCustomerData,
      updated: false,
    };
  }

  /**
   * Checks if data is a duplicate in the given list
   * @param {CustomerDataFields} data - Data to check for duplication
   * @param {CustomerDataFields[]} dataList - List of existing data to check against
   * @returns {boolean} True if data is a duplicate, false otherwise
   */
  static isDuplicate(
    data: CustomerDataFields,
    dataList: CustomerDataFields[],
  ): boolean {
    if (!Array.isArray(dataList)) {
      Logger.error('Invalid data list', {
        dataList,
      });
      return false;
    }
    return dataList?.some(item => data.equals(CustomerDataFields.create(item)));
  }

  /**
   * Merges customer data into a single obje^ct with arrays of unique values
   * @param {ICustomerDataFields[]} dataList - List of customer data to merge
   * @returns {TCustomerDataArrays} Merged customer data with arrays of unique values
   */
  private static mergeCustomerData(
    dataList: ICustomerDataFields[],
  ): TCustomerDataArrays {
    const mergedData: CustomerDataSet = {
      as: new Set<string>(),
      cc: new Set<string>(),
      ct: new Set<string>(),
      dob: new Set<string>(),
      em: new Set<string>(),
      fn: new Set<string>(),
      ge: new Set<string>(),
      id: new Set<string>(),
      ln: new Set<string>(),
      ph: new Set<string>(),
      rc: new Set<string>(),
      rg: new Set<string>(),
      street: new Set<string>(),
      zip: new Set<string>(),
    };

    if (!dataList || dataList.length === 0 || !dataList[0]?.ds) {
      return {
        cc: [],
        ct: [],
        dob: [],
        ds: [],
        em: [],
        fn: [],
        ge: [],
        id: [],
        ln: [],
        ph: [],
        rc: [],
        rg: [],
        street: [],
        zip: [],
      };
    }
    dataList?.forEach(data => {
      if (data.ds) mergedData.as?.add(data.ds);
      if (data.id) mergedData.id?.add(data.id);
      if (data.fn) mergedData.fn?.add(data.fn);
      if (data.ln) mergedData.ln?.add(data.ln);
      if (data.em) mergedData.em?.add(data.em);
      if (data.ph) mergedData.ph?.add(data.ph);
      if (data.dob) mergedData.dob?.add(data.dob);
      if (data.ge) mergedData.ge?.add(data.ge);
      if (data.street) mergedData.street?.add(data.street);
      if (data.ct) mergedData.ct?.add(data.ct);
      if (data.cc) mergedData.cc?.add(data.cc);
      if (data.rg) mergedData.rg?.add(data.rg);
      if (data.rc) mergedData.rc?.add(data.rc);
      if (data.zip) mergedData.zip?.add(String(data.zip));
    });

    return {
      cc: Array.from(mergedData.cc || []),
      ct: Array.from(mergedData.ct || []),
      dob: Array.from(mergedData.dob || []),
      ds: Array.from(mergedData.as || []),
      em: Array.from(mergedData.em || []),
      fn: Array.from(mergedData.fn || []),
      ge: Array.from(mergedData.ge || []),
      id: Array.from(mergedData.id || []),
      ln: Array.from(mergedData.ln || []),
      ph: Array.from(mergedData.ph || []),
      rc: Array.from(mergedData.rc || []),
      rg: Array.from(mergedData.rg || []),
      street: Array.from(mergedData.street || []),
      zip: Array.from((mergedData.zip as Set<string>) || []),
    };
  }

  /**
   * Filters out invalid data and creates CustomerDataFields instances
   * @param {ICustomerDataFields[]} dataList - List of customer data to filter and convert
   * @returns {ICustomerDataFields  []} Filtered and converted customer data
   */
  private static filterValidData(
    dataList: ICustomerDataFields[],
  ): ICustomerDataFields[] {
    if (!Array.isArray(dataList)) {
      Logger.error('Invalid data list', {
        dataList,
      });
      return [];
    }
    const filteredData = dataList
      ?.filter(data => data.ds !== 'empty')
      ?.map(data => CustomerDataFields.create(data));
    return filteredData?.filter(data => !!data);
  }
}
